import { PAGINATION_LIMIT } from "#lib/pagination";
import { fetchProfiles } from "#api/profiles";
import { findFavoriteProfiles } from "#entities/account";
import { CreatorWithFavs } from "#api/creators";

// the original page is a clusterfuck which pulls an entire artist list
// and does sorting/ordering/filtering on client
// rewriting it requires rewriting backend endpoints
// so for now it does the same
// TODO: rewrite it on backend
let allArtists: Awaited<ReturnType<typeof fetchProfiles>> | undefined =
  undefined;

export interface IGetArtistsArgs {
  service?: string;
  offset?: number;
  order?: "asc" | "desc";
  sort_by?: "favorited" | "indexed" | "updated" | "name" | "service";
  query?: string;
}

interface IGetArtistsResult {
  artists: (Awaited<ReturnType<typeof fetchProfiles>>[number] & { isFavorite: boolean })[];
  count: number;
}

export async function getArtists({
  offset = 0,
  service,
  order = "desc",
  sort_by = "favorited",
  query,
}: IGetArtistsArgs): Promise<IGetArtistsResult> {
  if (!allArtists) {
    allArtists = await fetchProfiles();
  }

  const normalizedQuery = query?.trim().toLowerCase();
  // MDN recommends doing this for large arrays
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
  const compare = new Intl.Collator().compare;

  const filteredArtists = allArtists
    .filter((creator) => {
      let isServiceMatched = true

      if (service) {
        isServiceMatched = creator.service === service;
      }

      let isQueryMatched = true

      if (normalizedQuery) {
        const normalizedName = creator.name.trim().toLowerCase();
        const normalizedID = creator.id.trim().toLowerCase();

        isQueryMatched = normalizedID.includes(normalizedQuery) || normalizedName.includes(normalizedQuery);
      }

      return isServiceMatched && isQueryMatched;
    })
    .sort((prev, next) => {
      switch (sort_by) {
        case "favorited": {
          return prev.favorited === next.favorited
            ? 0
            : prev.favorited > next.favorited
              ? 1
              : -1;
        }

        case "service": {
          return compare(prev.service, next.service);
        }

        case "name": {
          return compare(prev.name, next.name);
        }

        case "indexed": {
          const prevIndexed = prev.indexed * 1000;
          const nextIndexed = next.indexed * 1000;

          return prevIndexed === nextIndexed
            ? 0
            : prevIndexed > nextIndexed
              ? 1
              : -1;
        }
        case "updated": {
          const prevUpdated = prev.updated * 1000;
          const nextUpdated = next.updated * 1000;

          return prevUpdated === nextUpdated
            ? 0
            : prevUpdated > nextUpdated
              ? 1
              : -1;
        }

        default: {
          throw new Error(`Unknown sorting type "${sort_by}".`);
        }
      }
    });

  // artists are only sorted by one field
  // so we can get away with "desc" just slicing off the end
  // without changing sorting logic
  const slicedArtists =
    order === "asc"
      ? filteredArtists.slice(offset, offset + PAGINATION_LIMIT)
      : filteredArtists
        .slice(
          -offset + -PAGINATION_LIMIT,
          offset === 0 ? undefined : -offset
        )
        .reverse();
  const profilesData: Parameters<typeof findFavoriteProfiles>[0] =
    slicedArtists.map(({ id, service }) => {
      return { id, service };
    });
  const favArtists = await findFavoriteProfiles(profilesData);
  const resultArtists: IGetArtistsResult["artists"] = slicedArtists.map(
    (artist) => {
      const fav = favArtists.find(
        ({ id, service }) => id === artist.id && service === artist.service
      );

      return {
        ...artist,
        isFavorite: fav === undefined ? false : true,
      };
    }
  );
  const count = filteredArtists.length;

  return { artists: resultArtists, count };
}
